import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms'; // Import FormsModule for ngModel
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DisplayMessage, GenericValidator, ValidationMessages} from '../../shared/validators/generic-validator';
import { EmailjsService } from '../../shared/services/emailjs/emailjs.service';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../../shared/services/toast/toast.service';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.css'],
})
export class ContactComponent implements OnInit {
  isSubmitting = false;
  contactForm!: FormGroup;
  displayMessage: DisplayMessage = {};
  private genericValidator: GenericValidator;
  private destroyRef = inject(DestroyRef);

  // Define the validation messages using translation keys
  private validationMessages: ValidationMessages = {
    name: {
      required: 'contact.form.validation.nameRequired',
      minlength: 'contact.form.validation.nameMinLength',
      maxlength: 'contact.form.validation.nameMaxLength',
    },
    email: {
      required: 'contact.form.validation.emailRequired',
      email: 'contact.form.validation.emailInvalid',
    },
    subject: {
      required: 'contact.form.validation.subjectRequired',
      minlength: 'contact.form.validation.subjectMinLength',
      maxlength: 'contact.form.validation.subjectMaxLength',
    },
    message: {
      required: 'contact.form.validation.messageRequired',
      minlength: 'contact.form.validation.messageMinLength',
      maxlength: 'contact.form.validation.messageMaxLength',
    },
  };

  constructor(private fb: FormBuilder,
    private emailjsService: EmailjsService,
    private translate: TranslateService,
    private toastService: ToastService) {
    this.genericValidator = new GenericValidator(this.validationMessages);
  }

  ngOnInit() {
    this.contactForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
      email: [
        '', 
        [
          Validators.required, 
          Validators.email
        ]
      ],
      subject: [
        '', 
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(50),
        ]
      ],
      message: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(500),
        ],
      ],
    });

    this.contactForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
      this.displayMessage = this.genericValidator.processMessages(this.contactForm);
    });
  }

  onSubmit() {
    if (!this.contactForm.valid) {
      this.displayMessage = this.genericValidator.processMessages(this.contactForm);
      return;
    }

    this.isSubmitting = true;
    this.sendEmail(this.contactForm);
  }

  private sendEmail(contactForm: FormGroup) {
    const name = contactForm.get('name')?.value;
    const email = contactForm.get('email')?.value;
    const subject = contactForm.get('subject')?.value;
    const message = contactForm.get('message')?.value;

    this.emailjsService
      .sendEmail(name, email, subject, message)
      .then(() => {
        this.isSubmitting = false;
        this.contactForm.reset();
        this.toastService.showToast(
          this.translate.instant('contact.form.toast.successTitle'),
          this.translate.instant('contact.form.toast.successMessage'),
          'success',
          5000
        );
      })
      .catch(() => {
        this.isSubmitting = false;
        this.toastService.showToast(
          this.translate.instant('contact.form.toast.errorTitle'),
          this.translate.instant('contact.form.toast.errorMessage'),
          'error',
          5000
        );
      });
  }
}

